import { getGraphBearerToken } from './token-service';
import { getContentServiceUrl, getSearchHintsServiceUrl } from './urls';
import { BaseContentResponse } from 'lib/services/models/content-response';
import { getResponseObject } from './utils/utils';
import { ContentType } from './models/types';
import {
  normalizeSearchResponseUrls,
  SearchRecord,
  SearchResponse,
} from './models/search-response';
import { encodeURIComponentSafe, normalizeGuid } from 'lib/utils/string-utils';
import { pickBy } from 'lodash';
import { SearchHintsResponse } from './models/search-hints-response';

export interface OptionalContentParams {
  language?: string;
  responseMetadataIds?: string[];
  searchConfigurationId?: string;
  timeout?: number;
}

export async function getContent(
  siteId: string,
  contentId: string,
  {
    language = 'en',
    responseMetadataIds = [],
    searchConfigurationId,
    timeout: timeoutMS,
  }: OptionalContentParams = {}
) {
  const contentServiceUrl = getContentServiceUrl(siteId);

  const queryParams = new URLSearchParams(
    pickBy({
      Language: language,
      ResponseMetadataIds: responseMetadataIds.join(','),
      SearchConfigurationId: searchConfigurationId,
    }) as Record<string, string>
  );
  const url = `${contentServiceUrl}/${contentId}?${queryParams}`;
  const bearerToken = await getGraphBearerToken(siteId);
  if (!bearerToken) {
    console.error('No bearer token found for site:', siteId);
    return null;
  }
  const data = await getResponseObject<BaseContentResponse>(url, bearerToken, timeoutMS);
  return data;
}

export type SortField =
  | 'None'
  | 'Title'
  | 'HighestRated'
  | 'MostReviewed'
  | 'Quickest'
  | 'Newest'
  | string;

export type SortOrder = 'None' | 'Asc' | 'Desc' | string;

export type RelatedContentType = 'Standard' | 'Recommended' | 'RecommendedWithFallback' | string;

export interface OptionalSearchParams {
  language?: string;
  searchTerm?: string;
  contentIds?: string[];
  metadataIds?: string[];
  contentTypes?: ContentType[];
  pageSize?: number;
  pageIndex?: number;
  relatedContentType?: RelatedContentType;
  contentItem?: BaseContentResponse;
  sortBy?: SortField;
  sortDirection?: SortOrder;
  searchConfigurationId?: string;
  facetGroups?: SearchFacetGroup[];
  getFacets?: boolean;
  taggedSearchMatchAll?: boolean;
}

export interface SearchFacetGroup {
  name: string;
  facets: SearchFacetValue[];
}

export interface SearchFacetValue {
  name: string;
  facetId: string;
}

export interface RelatedSearchParams extends Omit<
  OptionalSearchParams,
  'relatedContentType' | 'contentItem'
> {
  relatedContentType: RelatedContentType;
  contentItem: BaseContentResponse;
}

function resolveRelatedSearch(
  relatedContentType: RelatedContentType,
  contentItem: BaseContentResponse,
  searchTerm: string,
  pageSize: number,
  contentIds: string[]
) {
  const recommendedContentIds = contentItem.RecommendedContentIds || [];

  const shouldUseRecommended =
    recommendedContentIds.length > 0 &&
    (relatedContentType === 'Recommended' || relatedContentType === 'RecommendedWithFallback');
  const shouldUseStandard =
    relatedContentType === 'Standard' ||
    (relatedContentType === 'RecommendedWithFallback' && recommendedContentIds.length === 0);

  if (shouldUseRecommended) {
    return { contentIds: recommendedContentIds, searchTerm, pageSize, searchType: undefined };
  }
  if (shouldUseStandard) {
    const resolvedSearchTerm = [searchTerm, contentItem.Title, ...contentItem.RelatedContentTags]
      .join(' ')
      .split(' ')
      .filter((word) => {
        const isNumeric = !Number.isNaN(Number(word));
        return word.length > 3 && !isNumeric;
      })
      .join(' ')
      .trim()
      .toLowerCase();
    return {
      contentIds,
      searchTerm: resolvedSearchTerm,
      pageSize: pageSize + 1,
      searchType: 1 as const,
    };
  }
  return null;
}

export async function getSearch(
  siteId: string,
  params: OptionalSearchParams | RelatedSearchParams = {}
) {
  const {
    language = 'en',
    searchTerm = '',
    metadataIds = [],
    contentTypes = ['All'],
    pageSize = 10,
    pageIndex = 1,
    relatedContentType,
    contentItem,
    sortBy = 'None',
    sortDirection = 'None',
    searchConfigurationId,
    facetGroups = [],
    getFacets = false,
    taggedSearchMatchAll,
  } = params;
  let contentIds = params.contentIds ?? [];
  const contentServiceUrl = getContentServiceUrl(siteId);
  const contentTypesString = encodeURIComponent(contentTypes.join(' '));
  const normalizedMetadataIds = (Array.isArray(metadataIds) ? metadataIds : [])
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
  const metadataIdsString = normalizedMetadataIds.join(' ');

  let finalSearchTerm = searchTerm;
  let finalPageSize = pageSize;
  let searchType: number | undefined;

  if (relatedContentType) {
    if (!contentItem) return null;
    const related = resolveRelatedSearch(
      relatedContentType,
      contentItem,
      searchTerm,
      pageSize,
      contentIds
    );
    if (!related) return null;
    contentIds = related.contentIds;
    finalSearchTerm = related.searchTerm;
    finalPageSize = related.pageSize;
    searchType = related.searchType;
  } else if (normalizedMetadataIds.length > 0) {
    searchType = 2;
  }
  const contentIdsString = contentIds.join(' ');

  const queryString = new URLSearchParams(
    pickBy({
      Language: language,
      pageSize: finalPageSize.toString(),
      pageIndex: pageIndex.toString(),
      SearchTerm: finalSearchTerm,
      ContentIds: contentIdsString,
      TaggedMetadataIds: metadataIdsString,
      ContentTypes: contentTypesString,
      SearchType: searchType,
      SortBy: sortBy,
      SortDirection: sortDirection,
      SearchConfigurationId: searchConfigurationId,
      TaggedSearchMatchAll: taggedSearchMatchAll?.toString().toLowerCase(),
    }) as Record<string, string>
  );
  let cleanedFacetGroups = facetGroups;
  if (cleanedFacetGroups.length === 0 && getFacets) {
    // We need at least an empty facet group to return facets.
    cleanedFacetGroups = [{ name: '', facets: [{ name: '', facetId: ' ' }] }];
  }

  const url = `${contentServiceUrl}?${queryString.toString()}&${cleanedFacetGroups
    .map(
      (x, i) =>
        `FacetGroups[${i}].GroupName=${encodeURIComponentSafe(x.name)}&FacetGroups[${i}].FacetIds=${x.facets.map((y) => y.facetId).join(encodeURIComponentSafe(' '))}`
    )
    .join('&')}`;
  const bearerToken = await getGraphBearerToken(siteId);
  if (!bearerToken) {
    console.error('No bearer token found for site:', siteId);
    return null;
  }

  const data = await getResponseObject<SearchResponse>(url, bearerToken);

  if (data && relatedContentType && contentItem) {
    const relatedContent = data?.Results.filter(
      (x) => normalizeGuid(x.ContentId) !== normalizeGuid(contentItem?.ContentId)
    );
    data.Results = relatedContent?.slice(0, pageSize ?? relatedContent.length) ?? [];
  }

  const normalizedData = data ? normalizeSearchResponseUrls(data) : data;

  return normalizedData;
}

// This is the current max page size for the content service.
const MAX_PAGE_SIZE = 100;

/**
 * Fetches results from the content service in batches because the content service has a max page size of 100.
 * @param siteId - The site ID to fetch results from.
 * @param batchNumber - The batch number to fetch results from.
 * @param batchSize - The batch size to fetch results for.
 * @param parameters - The remaining parameters to pass to the getSearch function.
 * @returns The results from the content service.
 */
export async function getBatchedSearchResults(
  siteId: string,
  // we use batch size instead of page size
  parameters: Omit<OptionalSearchParams, 'pageIndex' | 'pageSize'> & {
    batchNumber: number;
    batchSize: number;
  }
) {
  const pageSize = parameters.batchSize > MAX_PAGE_SIZE ? MAX_PAGE_SIZE : parameters.batchSize;
  if (parameters.batchSize % MAX_PAGE_SIZE > 0) {
    throw new Error(`Batch size ${parameters.batchSize} must be a multiple of ${MAX_PAGE_SIZE}`);
  }
  const pagesPerBatch = Math.floor(parameters.batchSize / MAX_PAGE_SIZE);

  const startPageNumber = (parameters.batchNumber - 1) * pagesPerBatch + 1;
  const endPageNumber = startPageNumber + pagesPerBatch - 1;

  const allResults: SearchRecord[] = [];

  for (let pageIndex = startPageNumber; pageIndex <= endPageNumber; pageIndex++) {
    const results = await getSearch(siteId, { ...parameters, pageIndex, pageSize });
    if (!results) {
      throw new Error(`Failed to fetch results for page ${pageIndex}`);
    }
    if (results.Results.length <= 0) {
      // Exit early if we have no results
      break;
    }
    allResults.push(...results.Results);
  }
  return allResults;
}

type AutoPopulationCriteria =
  | 'NewlyCreatedOrModified'
  | 'MostReviewed'
  | 'TopViewed'
  | 'TopRatedAndNewlyCreated'
  | 'TopRatedWithMoreThan500Views';

export interface OptionalSearchHintsRequest {
  SearchTerms?: string[];
  SearchTermMaxResults?: number;
  ContentMaxResults?: number;
  Language?: string;
  ContentType?: ContentType;
  CuratedContentIds?: string[];
  AutoPopulationCriteria?: AutoPopulationCriteria[];
  HintsPerContentType?: boolean;
  DisableSearchTerms?: boolean;
}

export async function getSearchHints(siteId: string, request: OptionalSearchHintsRequest) {
  const contentServiceUrl = getSearchHintsServiceUrl(siteId);
  const bearerToken = await getGraphBearerToken(siteId);
  if (!bearerToken) {
    console.error('No bearer token found for site:', siteId);
    return null;
  }
  const queryString = new URLSearchParams(
    pickBy({
      Language: request.Language ?? 'en',
      SearchTerms: request.SearchTerms?.join(','),
      SearchTermMaxResults: request.SearchTermMaxResults?.toString(),
      ContentMaxResults: request.ContentMaxResults?.toString(),
      ContentType: request.ContentType ?? 'All',
      CuratedContentIds: request.CuratedContentIds?.join(','),
      AutoPopulationCriteria: request.AutoPopulationCriteria?.join(','),
      HintsPerContentType: request.HintsPerContentType?.toString(),
      DisableSearchTerms: request.DisableSearchTerms?.toString(),
    }) as Record<string, string>
  );

  console.log(`${contentServiceUrl}?${queryString.toString()}`);
  const data = await getResponseObject<SearchHintsResponse>(
    `${`${contentServiceUrl}`}?${queryString.toString()}`,
    bearerToken
  );
  return data;
}
